import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EvidenceFile, Paginated, ResponseDetail } from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../../lib/api.js';
import { useOrg } from '../../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Spinner,
  formatBytes,
  formatDate,
  useToast,
} from '../../../components/ui.js';

/** Mirrors EVIDENCE_ALLOWED_TYPES (packages/types) by extension. */
const ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.heic,.doc,.docx,.xlsx,.pptx,.msg,.csv,.txt,.md,.html,.htm,.zip';

export function EvidencePanel({ response }: { response: ResponseDetail }) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const canWrite =
    (org.role === 'admin' || org.role === 'contributor') &&
    !['finalised', 'assessed', 'locked'].includes(response.status);

  const evidence = useQuery({
    queryKey: ['evidence', response.id],
    queryFn: () =>
      apiFetch<Paginated<EvidenceFile>>(`/orgs/${org.orgId}/evidence`, {
        query: { responseId: response.id, limit: 100 },
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['evidence', response.id] });
    void queryClient.invalidateQueries({ queryKey: ['response', response.id] });
    void queryClient.invalidateQueries({ queryKey: ['responses', response.assessmentId] });
  };
  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Evidence action failed', 'error');

  const upload = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<EvidenceFile>(`/responses/${response.id}/evidence`, {
        method: 'POST',
        formData,
      });
    },
    onSuccess: () => {
      toast('Evidence uploaded.', 'success');
      invalidate();
    },
    onError,
  });

  const [attachOpen, setAttachOpen] = useState(false);
  const link = useMutation({
    mutationFn: (evidenceId: string) =>
      apiFetch<EvidenceFile>(`/responses/${response.id}/evidence/link`, {
        method: 'POST',
        body: { evidenceId },
      }),
    onSuccess: () => {
      toast('Attached.', 'success');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['evidence-picker', response.id] });
    },
    onError,
  });

  const [supersedeTarget, setSupersedeTarget] = useState<EvidenceFile | null>(null);
  const supersedeInput = useRef<HTMLInputElement>(null);
  const supersede = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<EvidenceFile>(`/evidence/${id}/supersede`, { method: 'POST', formData });
    },
    onSuccess: () => {
      toast('Replacement uploaded — the old file is kept as superseded.', 'success');
      setSupersedeTarget(null);
      invalidate();
    },
    onError,
  });

  const [deleteTarget, setDeleteTarget] = useState<EvidenceFile | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/evidence/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteTarget(null);
      invalidate();
    },
    onError,
  });

  if (evidence.isLoading) return <Spinner label="Loading evidence" />;
  const items = evidence.data?.items ?? [];
  const active = items.filter((e) => e.retentionStatus === 'active');

  return (
    <div>
      {response.question.requiredEvidence && active.length === 0 && (
        <Card className="card" title="Evidence required">
          <p style={{ color: 'var(--color-warning)' }}>
            This question cannot be finalised until at least one evidence file is attached.
          </p>
          {response.question.evidenceGuidance && (
            <p style={{ color: 'var(--color-text-muted)' }}>{response.question.evidenceGuidance}</p>
          )}
        </Card>
      )}

      {items.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>No evidence uploaded yet.</p>
      )}
      {items.map((e) => (
        <div
          key={e.id}
          className={`evidence-item ${e.retentionStatus === 'superseded' ? 'superseded' : ''}`}
        >
          <div>
            <div className="name">
              <a href={apiUrl(`/evidence/${e.id}/download`)}>{e.fileName}</a>{' '}
              {e.locked && <Badge>locked</Badge>}
              {e.sourceEvidenceId && (
                <span title="Attached from another response">
                  <Badge tone="info">linked</Badge>
                </span>
              )}
              {e.retentionStatus !== 'active' && <Badge tone="neutral">{e.retentionStatus}</Badge>}
            </div>
            <div className="meta">
              {formatBytes(e.sizeBytes)} · uploaded {formatDate(e.uploadedAt)} · sha256{' '}
              {e.sha256.slice(0, 10)}…
            </div>
          </div>
          {canWrite && e.retentionStatus === 'active' && !e.locked && (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="secondary" onClick={() => setSupersedeTarget(e)}>
                Replace
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(e)}>
                Delete
              </Button>
            </div>
          )}
        </div>
      ))}

      {canWrite && (
        <div style={{ marginTop: 12 }}>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            aria-label="Upload evidence file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              loading={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              Upload evidence
            </Button>
            <Button variant="secondary" onClick={() => setAttachOpen(true)}>
              Attach existing
            </Button>
          </div>
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)', marginTop: 6 }}>
            PDF, images, Office documents, Outlook messages, CSV, text, HTML or ZIP · up to 50 MB
          </p>
        </div>
      )}
      {!canWrite && (
        <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)', marginTop: 8 }}>
          Evidence is read-only {['finalised', 'assessed', 'locked'].includes(response.status) ? 'after finalisation' : 'for your role'}.
        </p>
      )}

      <AttachExistingDialog
        open={attachOpen}
        responseId={response.id}
        attachedIds={items.map((e) => e.id)}
        pendingId={link.isPending ? link.variables : null}
        onAttach={(id) => link.mutate(id)}
        onClose={() => setAttachOpen(false)}
      />

      <Dialog
        open={!!supersedeTarget}
        onClose={() => setSupersedeTarget(null)}
        title={`Replace ${supersedeTarget?.fileName ?? ''}`}
      >
        <p>
          The current file stays in the audit trail marked as superseded; the replacement becomes
          the active evidence.
        </p>
        <input
          ref={supersedeInput}
          type="file"
          accept={ACCEPT}
          aria-label="Replacement file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && supersedeTarget) supersede.mutate({ id: supersedeTarget.id, file });
          }}
        />
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setSupersedeTarget(null)}>
            Cancel
          </Button>
        </div>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete evidence?">
        <p>
          {deleteTarget?.fileName} will be marked as deleted. The stored file is retained for the
          audit trail but no longer counts as evidence.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function AttachExistingDialog({
  open,
  responseId,
  attachedIds,
  pendingId,
  onAttach,
  onClose,
}: {
  open: boolean;
  responseId: string;
  attachedIds: string[];
  pendingId: string | null | undefined;
  onAttach: (evidenceId: string) => void;
  onClose: () => void;
}) {
  const { org } = useOrg();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  // Debounce so each keystroke doesn't hit the library endpoint.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const candidates = useQuery({
    enabled: open,
    queryKey: ['evidence-picker', responseId, q],
    queryFn: () =>
      apiFetch<Paginated<EvidenceFile>>(`/orgs/${org.orgId}/evidence`, {
        query: {
          q: q || undefined,
          excludeResponseId: responseId,
          retentionStatus: 'active',
          limit: 50,
        },
      }),
  });
  const rows = (candidates.data?.items ?? []).filter((e) => !attachedIds.includes(e.id));

  return (
    <Dialog open={open} onClose={onClose} title="Attach existing evidence">
      <p style={{ color: 'var(--color-text-muted)' }}>
        Attach a file already in your evidence library. The stored file is shared, not copied.
      </p>
      <Field label="Search by file name">
        {(props) => (
          <Input
            {...props}
            type="search"
            value={search}
            autoFocus
            placeholder="e.g. access-policy.pdf"
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </Field>
      {candidates.isLoading && <Spinner label="Searching evidence" />}
      {!candidates.isLoading && rows.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>
          {q ? 'No files match that name.' : 'No other active evidence files to attach.'}
        </p>
      )}
      {rows.map((e) => (
        <div key={e.id} className="evidence-item">
          <div>
            <div className="name">
              {e.fileName}{' '}
              {e.sourceEvidenceId && (
                <span title="Attached from another response">
                  <Badge tone="info">linked</Badge>
                </span>
              )}
            </div>
            <div className="meta">
              {formatBytes(e.sizeBytes)} · uploaded {formatDate(e.uploadedAt)} ·{' '}
              {e.responseId ? 'on a response' : e.corpusItemId ? 'on a corpus item' : 'unattached'}
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            loading={pendingId === e.id}
            disabled={!!pendingId}
            onClick={() => onAttach(e.id)}
            aria-label={`Attach ${e.fileName}`}
          >
            Attach
          </Button>
        </div>
      ))}
      <div className="dialog-actions">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}
